/**
 * AmbassadorMintBanner
 *
 * Displayed above the regular mint UI when an ambassador has an unclaimed
 * allocation for this launch. Shows claim button and X post submission after claiming.
 */

import React, { useState } from 'react';
import { Button } from '../../ui/components/Button';

interface AmbassadorMintBannerProps {
  allocationId: string;
  claimed: boolean;
  xPostUrl: string | null;
  xPostDeadlineAt: string | null;
  lockExpiresAt: string | null;
  expiresAt: string | null;
  onClaim: (allocationId: string) => Promise<void>;
  onSubmitXPost: (allocationId: string, url: string) => Promise<void>;
  claimLoading?: boolean;
}

export const AmbassadorMintBanner: React.FC<AmbassadorMintBannerProps> = ({
  allocationId,
  claimed,
  xPostUrl,
  xPostDeadlineAt,
  lockExpiresAt,
  expiresAt,
  onClaim,
  onSubmitXPost,
  claimLoading,
}) => {
  const [postUrl, setPostUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(!!xPostUrl);

  const daysUntilExpiry = expiresAt
    ? Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  async function handleSubmitPost() {
    if (!postUrl.trim()) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await onSubmitXPost(allocationId, postUrl.trim());
      setSubmitted(true);
    } catch (err: any) {
      setSubmitError(err?.response?.data?.error || 'Failed to submit X post URL');
    } finally {
      setSubmitting(false);
    }
  }

  if (claimed && submitted) {
    // Already claimed and posted
    return (
      <div className="border-2 border-success-300 bg-success-50 rounded-xl p-5 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-success-700 text-lg">&#10003;</span>
          <h4 className="text-body font-body-bold text-success-700">Ambassador Mint Claimed</h4>
        </div>
        <p className="text-body text-success-600">
          Your free NFT has been claimed and your X post has been submitted. Thank you for being an ambassador!
        </p>
        {lockExpiresAt && (
          <p className="text-caption text-success-600 mt-2">
            Hold period until {new Date(lockExpiresAt).toLocaleDateString()}
          </p>
        )}
      </div>
    );
  }

  if (claimed && !submitted) {
    // Claimed but need to submit X post
    return (
      <div className="border-2 border-warning-300 bg-warning-50 rounded-xl p-5 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-warning-700 text-lg">&#9888;</span>
          <h4 className="text-body font-body-bold text-warning-700">Submit Your X Post</h4>
        </div>
        <p className="text-body text-warning-600 mb-4">
          Your free NFT has been claimed. Please post about this project on X and submit the link below within 24 hours.
        </p>
        <div className="flex gap-2">
          <input
            type="url"
            value={postUrl}
            onChange={(e) => setPostUrl(e.target.value)}
            placeholder="https://x.com/yourpost/status/..."
            className="flex-1 px-3 py-2 border border-neutral-border rounded-md text-body text-default-font bg-default-background"
          />
          <Button onClick={handleSubmitPost} disabled={submitting || !postUrl.trim()} size="medium">
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </div>
        {submitError && <p className="text-caption text-error-600 mt-2">{submitError}</p>}
        {xPostDeadlineAt && (
          <p className="text-caption text-warning-600 mt-2">
            Deadline: {new Date(xPostDeadlineAt).toLocaleString()}
          </p>
        )}
      </div>
    );
  }

  // Not claimed yet - show claim button
  return (
    <div className="border-2 border-brand-300 bg-brand-50 rounded-xl p-5 mb-6">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-brand-700 text-lg">&#9733;</span>
        <h4 className="text-body font-body-bold text-brand-700">You've been selected as an Ambassador!</h4>
      </div>
      <p className="text-body text-brand-600 mb-2">
        Claim your free NFT below. After claiming, you'll need to post about this project on X within 24 hours.
      </p>
      <p className="text-caption text-brand-500 mb-4">
        This NFT must be held for at least 3 months as part of the Ambassadors Program.
        {daysUntilExpiry !== null && ` Claim expires in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''}.`}
      </p>
      <Button onClick={() => onClaim(allocationId)} disabled={claimLoading} size="medium">
        {claimLoading ? 'Claiming...' : 'Claim Free Mint'}
      </Button>
    </div>
  );
};
